import React, { ReactElement, useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { Resolver, useForm } from 'react-hook-form';
import {
  IAuthenticateForm,
  IFormElementOptionProp,
  initialAuthenticateFormValues,
} from 'models/form';
import { yupResolver } from '@hookform/resolvers/yup';
import { EButtonVariants } from 'constants/Buttons';
import Button from 'components/atoms/Button';
import { Box } from '@mui/system';
import { AuthTemplate } from 'components/templates/AuthTemplate';
import { ERouteLinks } from 'models/route';
import { useNavigate } from 'react-router-dom';
import { useAuthenticationSetup } from 'pages/SystemSettingsPage/AuthenticationSetupPage/hooks';
import { RouterLink } from 'components/atoms/RouterLink';
import { FormCode } from 'components/molecules/FormCode';
import { useAuth } from 'models/authContext';
import { Typography } from '@mui/material';
import { FormCheckbox } from 'components/molecules/FormCheckbox';
import { FormSelect } from 'components/molecules/FormSelect';
import { ItrustedDeviceOptions } from 'models/settings';
import { v4 as uuidv4 } from 'uuid';
import { getDeviceName } from 'helpers/userAgent';
import { FC_SYSTEM_DEVICE_ID } from 'pages/LoginPage';
import { useTrustedDevice } from 'models/trustedDeviceContext';
import { useTranslations } from 'hooks/useTranslations';
import useForgotPasswordFormSchema from 'pages/ForgotPasswordPage/schemaValidation';
import {
  StyledLanguageSelectOuterWrapper,
  StyledLanguageSelectWrapper,
} from 'components/templates/AuthTemplate/styles';
import { LanguageSelect } from 'components/molecules/LanguageSelect';
import { AuthLogoContainer } from 'components/organisms/AuthLogoContainer';

export interface IAuthenticatorPage {
  offlineQRCodeBase64: string | null;
  email: string;
}

const AuthenticatorPage = ({
  offlineQRCodeBase64,
  email,
}: IAuthenticatorPage): ReactElement => {
  const { schema } = useForgotPasswordFormSchema();
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
    clearErrors,
    setError,
  } = useForm<IAuthenticateForm>({
    defaultValues: initialAuthenticateFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<IAuthenticateForm>,
  });
  const navigate = useNavigate();
  const { verifyAuthCode } = useAuth();
  const { t } = useTranslations();
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const { getEnabledTrustedDeviceOptionsData, trustedDeviceOptions } =
    useAuthenticationSetup();
  const { addTrustedDevice } = useTrustedDevice();
  const [showCode, setShowCode] = useState(false);

  const getTrustedDeviceOptionId = () => {
    if (!trustedDeviceOptions?.length) return undefined;
    const option = trustedDeviceOptions.sort((a, b) => a.daysToExpire! - b.daysToExpire!);
    if (option.length) {
      return option[0].uniqueId;
    }
    return undefined;
  };

  const defaultValue: string | undefined = getTrustedDeviceOptionId();

  const mapTrustedDeviceOptionsToOptions = (
    options: ItrustedDeviceOptions[] | null,
  ): IFormElementOptionProp[] => {
    if (options === null) {
      return [];
    }
    return options.map((option) => ({
      label: option.daysToExpire ? option.daysToExpire.toString() : '0',
      value: option.uniqueId || '',
    }));
  };

  const [formFields, setFormFields] = useState<IAuthenticateForm>(
    initialAuthenticateFormValues,
  );

  useEffect(() => {
    const subscription = watch(() => setFormFields(getValues() as IAuthenticateForm));
    return () => subscription.unsubscribe();
  }, [watch]);

  const onSubmit = handleSubmit(async (data: IAuthenticateForm) => {
    setIsLoginLoading(true);
    const codeArr = Object.values(data.code).reduce((acc, curr) => acc.toString() + curr);
    verifyAuthCode(
      {
        email,
        code: codeArr,
        trustedDeviceTimeOptionUniqueId: data.isRememberDevice
          ? data.trustedDeviceTimeOptionUniqueId
          : undefined,
        deviceName: getDeviceName(),
      },
      () => {
        if (data.isRememberDevice) {
          const deviceToken = uuidv4();
          addTrustedDevice(
            {
              deviceToken,
              title: getDeviceName(),
              trustedDeviceTimeOptionUniqueId: data.trustedDeviceTimeOptionUniqueId,
            },
            () => {
              localStorage.setItem(FC_SYSTEM_DEVICE_ID, deviceToken);
            },
          );
        }
        navigate(ERouteLinks.Dashboard);
      },
      () => {
        setError('code', {
          type: 'custom',
          message: t('login.TwoFactorAuthenticationIncorrect.label'),
        });
      },
    );
    setIsLoginLoading(false);
  });

  useEffect(() => {
    getEnabledTrustedDeviceOptionsData();
  }, []);

  const rememberMe = trustedDeviceOptions?.length ? (
    <Grid sx={{ display: "flex", alignItems: "center" }}>
      <Box>
        <FormCheckbox
          name="isRememberDevice"
          label={t('login.isRememberDevice.label')}
          onChange={(value) => {
            if (value) {
              setValue('trustedDeviceTimeOptionUniqueId', defaultValue);
            } else {
              setValue('trustedDeviceTimeOptionUniqueId', '');
            }
          }}
          control={control}
          errors={errors}
          withValidation={false}
        />
      </Box>
      <Box sx={{ height: "32px", marginTop: "-8px" }}>
        <FormSelect
          name="trustedDeviceTimeOptionUniqueId"
          options={mapTrustedDeviceOptionsToOptions(trustedDeviceOptions)}
          setValue={setValue}
          control={control}
          errors={errors}
          disabled={!formFields.isRememberDevice}
          withValidation={!formFields.isRememberDevice}
          canBeEmpty={false}
        />
      </Box>
      <Typography variant="caption" sx={{ ml: 2 }}>
        {t('login.days.label')}
      </Typography>
    </Grid>
  ) : (
    ''
  );

  return (
    <AuthTemplate>
      <Grid sx={{ py: 2, px: { xs: 2, xl: 20 }, justifyContent: "center", alignItems: "flex-start", alignContent: "stretch", pb: 5 }}>
        <Grid container>
          <StyledLanguageSelectOuterWrapper>
            <StyledLanguageSelectWrapper>
              <LanguageSelect isVisible />
            </StyledLanguageSelectWrapper>
          </StyledLanguageSelectOuterWrapper>
          <AuthLogoContainer />
        </Grid>

        <Grid container>
          <Grid sx={{ alignItems: "flex-start", m: "auto", gap: 1 }}>
            <Typography variant="h4" sx={{ mx: "auto", mb: 1 }}>
              {t('login.authenticator.label')}
            </Typography>
            {!offlineQRCodeBase64 && !showCode ? (
              <>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body1">
                    {t('login.authenticator.scanQRCode1.label')}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body1">
                    {t('login.authenticator.scanQRCode2.label')}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body1">
                    {t('login.authenticator.scanQRCode3.label')}
                  </Typography>
                </Grid>
                <Grid sx={{ textAlign: "center", my: 1 }}>
                  <img alt="qr" src={`data:image/jpeg;base64,${offlineQRCodeBase64}`} />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Button
                    label={t('login.next.button')}
                    variant={EButtonVariants.contained}
                    onClick={() => setShowCode(true)}
                    isLoading={isLoginLoading}
                    fullWidth
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid sx={{ textAlign: "center" }}>
                  <Typography variant="body1" sx={{ m: 'auto' }}>
                    {t('login.verifyAccountAuthenticatorApp.label')}
                  </Typography>
                </Grid>
                <Grid sx={{ mt: 1 }}>
                  <FormCode
                    name="code"
                    label={t('login.code.label')}
                    required
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    startFocus={true}
                    clearErrors={clearErrors}
                    disabled={isLoginLoading}
                  />
                </Grid>

                {rememberMe}

                <Grid sx={{ mt: 3 }}>
                  <Button
                    label={t('login.verify.button')}
                    variant={EButtonVariants.contained}
                    onClick={() => onSubmit()}
                    isLoading={isLoginLoading}
                    fullWidth
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Grid sx={{ mt: 3, justifyContent: "center" }}>
            <RouterLink
              to={ERouteLinks.AuthWithSlash}
              label={t('login.backLogin.button')}
              replace
              forceRefresh
            />
          </Grid>
        </Grid>
      </Grid>
    </AuthTemplate>
  );
};
export { AuthenticatorPage };
